/**
 * MatterListNavigationStore
 *
 * Zustand store for tracking which matter list view was last active.
 * Used by the details sidebar back button to navigate deterministically
 * to the correct view (e.g., /?view=today) instead of using router.back().
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MatterListId } from '@/lib/types/matter-navigation';

/** Valid matter list view IDs */
const VALID_VIEW_IDS: Set<string> = new Set<string>([
  'today',
  'upcoming',
  'anytime',
  'all',
  'archive'
]);

/** Type guard to check if a string is a valid MatterListId */
export function isMatterListId(value: string): value is MatterListId {
  return VALID_VIEW_IDS.has(value);
}

interface MatterListState {
  /** Last active matter list view */
  lastActiveList: MatterListId;
}

interface MatterListActions {
  /** Set the currently active matter list view */
  setActiveMatterList: (matterList: MatterListId) => void;
  /** Read and return the last active list (defaults to 'all') */
  getLastActiveList: () => MatterListId;
}

type MatterListStore = MatterListState & MatterListActions;

const DEFAULT_VIEW: MatterListId = 'all';

export const useMatterListNavigationStore = create<MatterListStore>()(
  devtools(
    (set, get) => ({
      lastActiveList: DEFAULT_VIEW,

      setActiveMatterList: (matterList: MatterListId) => {
        set({ lastActiveList: matterList }, false, 'setActiveMatterList');
      },

      getLastActiveList: () => {
        return get().lastActiveList ?? DEFAULT_VIEW;
      }
    }),
    {
      name: 'MatterListNavigationStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

/** Non-hook getter for use outside components (e.g., event handlers) */
export const getMatterListNavigationActions = () => ({
  setActiveMatterList:
    useMatterListNavigationStore.getState().setActiveMatterList,
  getLastActiveList: useMatterListNavigationStore.getState().getLastActiveList
});
